import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Columns3, Plus } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";

import {
  ApiError,
  type CustomFieldDefinition,
  type CustomFieldType,
  customFieldsApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/settings/custom-fields")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") throw redirect({ to: "/settings/tags" });
  },
  component: CustomFieldsPage,
});

const ENTITIES = [
  ["CRM", ["accounts", "contacts", "deals", "followups", "notes", "tags"]],
  [
    "ERP",
    [
      "products",
      "raw_materials",
      "warehouses",
      "inventory",
      "inventory_adjustments",
      "orders",
      "order_lines",
      "manufacturing_jobs",
      "dispatches",
      "deliveries",
      "delivery_lines",
      "drivers",
      "transports",
    ],
  ],
] as const;

const IMPORT_ENTITIES = new Set(["accounts", "contacts", "products", "raw_materials"]);
const EXPORT_ENTITIES = new Set(["accounts", "contacts", "orders", "dispatches"]);
const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "short_text", label: "Short text" },
  { value: "rich_text", label: "Rich text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "picklist", label: "Picklist" },
];

function title(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function keyFromLabel(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

function CustomFieldsPage() {
  const queryClient = useQueryClient();
  const [entity, setEntity] = useState("accounts");
  const [selectedId, setSelectedId] = useState<string>("new");
  const query = useQuery({
    queryKey: ["custom-fields", entity],
    queryFn: () => customFieldsApi.definitions(entity, true),
  });
  const selected = query.data?.items.find((item) => item.id === selectedId) ?? null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["custom-fields", entity] });

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold"><Columns3 className="size-5 opacity-70" />Custom fields</h2>
          <p className="text-sm opacity-60">Add optional typed fields without changing or invalidating existing records.</p>
        </div>
        <button type="button" onClick={() => setSelectedId("new")} className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white"><Plus className="size-4" />New field</button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <label className="block text-sm"><span className="mb-1 block font-medium">Entity</span><select className="form-input w-full" value={entity} onChange={(event) => { setEntity(event.target.value); setSelectedId("new"); }}>
            {ENTITIES.map(([group, entities]) => <optgroup key={group} label={group}>{entities.map((value) => <option key={value} value={value}>{title(value)}</option>)}</optgroup>)}
          </select></label>
          <div className="space-y-2">
            {query.data?.items.map((field) => <button key={field.id} type="button" onClick={() => setSelectedId(field.id)} className={`min-h-11 w-full rounded-lg border p-3 text-left ${selectedId === field.id ? "border-[var(--accent)] bg-[var(--hover)]" : "border-[var(--border)]"}`}><span className="block font-medium">{field.label}</span><span className="block text-xs opacity-60">{title(field.field_type)} · {field.key}{!field.is_active ? " · inactive" : ""}</span></button>)}
            {!query.isLoading && !query.data?.items.length && <p className="rounded-lg border border-dashed border-[var(--border)] p-4 text-sm opacity-60">No custom fields for {title(entity)}.</p>}
          </div>
        </aside>
        <FieldEditor key={`${entity}-${selected?.id ?? "new"}`} entity={entity} field={selected} onSaved={(saved) => { refresh(); setSelectedId(saved.id); }} />
      </div>
    </div>
  );
}

function FieldEditor({ entity, field, onSaved }: { entity: string; field: CustomFieldDefinition | null; onSaved: (field: CustomFieldDefinition) => void }) {
  const [label, setLabel] = useState(field?.label ?? "");
  const [key, setKey] = useState(field?.key ?? "");
  const [keyEdited, setKeyEdited] = useState(Boolean(field));
  const [fieldType, setFieldType] = useState<CustomFieldType>(field?.field_type ?? "short_text");
  const [helpText, setHelpText] = useState(field?.help_text ?? "");
  const [choices, setChoices] = useState((field?.picklist_options ?? []).join("\n"));
  const [includeImport, setIncludeImport] = useState(field?.include_in_import ?? false);
  const [includeExport, setIncludeExport] = useState(field?.include_in_export ?? false);
  const [isActive, setIsActive] = useState(field?.is_active ?? true);
  const picklistOptions = useMemo(() => choices.split("\n").map((item) => item.trim()).filter(Boolean), [choices]);

  const mutation = useMutation({
    mutationFn: () => field ? customFieldsApi.update(field.id, {
      label,
      help_text: helpText || null,
      ...(field.field_type === "picklist" ? { picklist_options: picklistOptions } : {}),
      include_in_import: includeImport,
      include_in_export: includeExport,
      is_active: isActive,
    }) : customFieldsApi.create({ entity_type: entity, key, label, field_type: fieldType, help_text: helpText || null, picklist_options: fieldType === "picklist" ? picklistOptions : [], include_in_import: includeImport, include_in_export: includeExport }),
    onSuccess: (saved) => { toast.success(field ? "Custom field updated." : "Custom field created."); onSaved(saved); },
    onError: (cause) => toast.error(cause instanceof ApiError ? cause.message : "Could not save the custom field."),
  });
  const submit = (event: FormEvent) => { event.preventDefault(); mutation.mutate(); };
  return <form onSubmit={submit} className="space-y-5 rounded-xl border border-[var(--border)] p-4 sm:p-5">
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="text-sm"><span className="mb-1 block">Label</span><input className="form-input w-full" required value={label} onChange={(event) => { const value = event.target.value; setLabel(value); if (!keyEdited) setKey(keyFromLabel(value)); }} /></label>
      <label className="text-sm"><span className="mb-1 block">Stable field key</span><input className="form-input w-full font-mono" required pattern="[a-z][a-z0-9_]*" disabled={Boolean(field)} value={key} onChange={(event) => { setKeyEdited(true); setKey(event.target.value.toLowerCase()); }} /><span className="mt-1 block text-xs opacity-55">Used in Excel headers and cannot change later.</span></label>
      <label className="text-sm"><span className="mb-1 block">Type</span><select className="form-input w-full" disabled={Boolean(field)} value={fieldType} onChange={(event) => setFieldType(event.target.value as CustomFieldType)}>{FIELD_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
      <label className="text-sm"><span className="mb-1 block">Help text</span><input className="form-input w-full" maxLength={500} value={helpText} onChange={(event) => setHelpText(event.target.value)} /></label>
    </div>
    {fieldType === "picklist" && <label className="block text-sm"><span className="mb-1 block">Choices—one per line</span><textarea className="form-input min-h-32 w-full" required value={choices} onChange={(event) => setChoices(event.target.value)} /></label>}
    <fieldset className="space-y-2 rounded-lg border border-[var(--border)] p-4"><legend className="px-1 text-sm font-medium">Excel</legend>
      <label className={`flex min-h-11 items-center gap-2 text-sm ${!IMPORT_ENTITIES.has(entity) ? "opacity-50" : ""}`}><input type="checkbox" checked={includeImport} disabled={!IMPORT_ENTITIES.has(entity)} onChange={(event) => setIncludeImport(event.target.checked)} />Include in import template and accept imported values</label>
      {!IMPORT_ENTITIES.has(entity) && <p className="text-xs opacity-55">This entity does not currently have a bulk-import workbook.</p>}
      <label className={`flex min-h-11 items-center gap-2 text-sm ${!EXPORT_ENTITIES.has(entity) ? "opacity-50" : ""}`}><input type="checkbox" checked={includeExport} disabled={!EXPORT_ENTITIES.has(entity)} onChange={(event) => setIncludeExport(event.target.checked)} />Include in scheduled/manual Excel exports</label>
      {!EXPORT_ENTITIES.has(entity) && <p className="text-xs opacity-55">This entity is not currently part of the export engine.</p>}
    </fieldset>
    {field && <label className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />Field is active</label>}
    <p className="rounded-md bg-[var(--hover)] p-3 text-xs opacity-75">Custom fields are always optional. Existing records remain valid and blank. Deactivating a field hides it but preserves historical values.</p>
    <button disabled={mutation.isPending || !label.trim() || !key.trim() || (fieldType === "picklist" && !picklistOptions.length)} className="min-h-11 rounded-md bg-[var(--accent)] px-5 text-sm font-medium text-white disabled:opacity-50">{field ? "Save field" : "Create field"}</button>
  </form>;
}
